import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import type { CalculationCPTType, ReportField } from '@/stores/type/calculationCPT.type'
import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { useCalculationStore } from '@/stores/calculation'
import type { CalculationType } from '@/stores/type/calculation.type'
import { useBaseStore } from '@haskoning/vue-basestore'

const api = useApi<CalculationCPTType>()

const baseUrl = API.CALCULATION_CPT.BASE
export const useCalculationCPTStore = defineStore('calculationCPT', () => {
	const baseStore = useBaseStore<CalculationCPTType>({ service: baseUrl, api })
	// State
	const currentStrippedItem = ref<CalculationCPTType | null>(null)
	const loading = ref(false)
	const filterFields = ref<string[]>(['name'])
	const exportReportDialog = ref(false)
	const exportReportLoading = ref(false)
	const exportExcelDialog = ref(false)
	const exportExcelLoading = ref(false)
	const reportFields = ref<ReportField[]>(['CSR', 'CRR', 'fos_liq', 's', 'cone_resistance'])
	const limits = ref<Record<string, number>>({})

	// Actions
	const resetAll = () => {
		baseStore.items.value = []
		baseStore.selectedItems.value = []
		baseStore.currentItem.value = null
		currentStrippedItem.value = null
	}

	const removeItemById = (id: number) => {
		const item = baseStore.items.value.find((item) => item.id === id)
		if (item) removeItem(item)
	}

	const addAndRemoveItem = (item: CalculationCPTType) => {
		if (baseStore.items.value.length >= 3) {
			removeItem(baseStore.items.value[0])
		}
		baseStore.items.value.push(item)
	}

	const fetchOrGetCalculationCPT = async (calculationCPT: CalculationCPTType) => {
		const fetched = baseStore.items.value.find((i) => calculationCPT.id === i.id)
		if (!fetched) {
			const response = await baseStore.fetchItem(calculationCPT.id)
			if (response) {
				addAndRemoveItem(response as CalculationCPTType)
				setCurrentItem(response as CalculationCPTType)
			}
		} else {
			setCurrentItem(fetched)
		}
	}

	const setCurrentItem = (item: CalculationCPTType | null) => {
		baseStore.currentItem.value = item
	}

	const setCurrentStrippedItem = (item: CalculationCPTType | null) => {
		currentStrippedItem.value = item
	}

	const removeItem = (item: CalculationCPTType) => {
		baseStore.items.value = baseStore.items.value.filter((i) => i.id !== item.id)
	}

	const removeItemsByCalculationId = (calculationId: number) => {
		const removeItems = baseStore.items.value.filter((item) => item.calculation === calculationId)
		const index = removeItems.findIndex((item) => item.id === baseStore.currentItem.value?.id)
		if (index !== -1) {
			setCurrentItem(null)
			setCurrentStrippedItem(null)
		}
		removeItems.forEach((item) => removeItem(item))
	}

	const toggleSelectedItem = (id: number) => {
		const index = baseStore.selectedItems.value.findIndex((selectedItem) => selectedItem.id === id)
		const calculationStore = useCalculationStore()
		const strippedItems = getStrippedItems.value
		const item = strippedItems.find((item) => item.id === id)
		if (!item) return
		if (index !== -1) {
			baseStore.selectedItems.value.splice(index, 1)
			calculationStore.removeSelectedItem(item.calculation)
		} else {
			baseStore.selectedItems.value.push(item)
			const selectedItemLength = getSelectedItemsByCalculationId(id).length
			const itemLength = getItemsByCalculationId(item.calculation).length
			if (selectedItemLength === itemLength) {
				calculationStore.addSelectedItem(item.calculation)
			}
		}
	}

	const selectAllItemsByCalculationId = (id: number) => {
		const calculationStore = useCalculationStore()
		const calculation = calculationStore.getCalculationById(id)
		const selected = calculation?.calculationcpt_set ?? []
		selected.forEach((item: CalculationCPTType) => {
			const index = baseStore.selectedItems.value.findIndex(
				(selectedItem) => selectedItem.id === item.id
			)
			if (index === -1) {
				baseStore.selectedItems.value.push(item)
			}
		})
	}

	const clearSelectedItemsByCalculationId = (id: number) => {
		const calculationStore = useCalculationStore()
		const calculation = calculationStore.getCalculationById(id)
		const selected = calculation?.calculationcpt_set ?? []
		selected.forEach((item: CalculationCPTType) => {
			const index = baseStore.selectedItems.value.findIndex(
				(selectedItem) => selectedItem.id === item.id
			)
			if (index !== -1) {
				baseStore.selectedItems.value.splice(index, 1)
			}
		})
	}

	const selectAllItems = () => {
		baseStore.selectedItems.value = [...getStrippedItems.value]
	}

	const clearSelectedItems = () => {
		baseStore.selectedItems.value = []
	}

	const toggleExportReportDialog = (value: boolean | null = null) => {
		exportReportDialog.value = value !== null ? value : !exportReportDialog.value
	}

	const toggleExportExcelDialog = (value: boolean | null = null) => {
		exportExcelDialog.value = value !== null ? value : !exportExcelDialog.value
	}

	const setLimit = (field: string, value: string) => {
		if (value === '' && field in limits.value) {
			delete limits.value[field]
			return
		} else {
			limits.value[field] = parseFloat(value) >= 0 ? parseFloat(value) : 0
		}
	}

	const exportReport = async () => {
		const reportUrl = `${API.CALCULATION_CPT.REPORT}?${getReportUrlQueryParams.value}`
		try {
			exportReportLoading.value = true
			await api.service(reportUrl).downloadBlob()
		} catch (e) {
			console.log(e)
		} finally {
			exportReportLoading.value = false
		}
	}

	const exportExcel = async () => {
		if (baseStore.selectedItems.value.length === 1) {
			const id = baseStore.selectedItems.value[0].id
			const excelUrl = `${API.CALCULATION_CPT.EXCEL(id)}`
			try {
				exportExcelLoading.value = true
				await api.service(excelUrl).downloadBlob()
			} catch (e) {
				console.log(e)
			} finally {
				exportExcelLoading.value = false
			}
		}
	}

	const getStrippedItems = computed((): CalculationCPTType[] => {
		const calculationStore = useCalculationStore()
		const result: CalculationCPTType[] = []
		calculationStore.items.forEach((calculation: CalculationType) => {
			result.push(...(calculation.calculationcpt_set ?? []))
		})
		return result
	})

	const hasCalculationCPT = (calculationId: number): boolean => {
		return baseStore.items.value.some((item) => item.calculation === calculationId)
	}

	const getSelectedItemsByCalculationId = (id: number): CalculationCPTType[] => {
		return baseStore.selectedItems.value.filter((item) => item.calculation === id)
	}

	const getItemsByCalculationId = (id: number): CalculationCPTType[] => {
		const calculationStore = useCalculationStore()
		const calculation = calculationStore.getCalculationById(id)
		return calculation?.calculationcpt_set ?? []
	}

	const getReportUrlQueryParams = computed(() => {
		const calculationCPTIds = baseStore.selectedItems.value.map((item) => item.id)
		const summaryFields = ['LPI_summary', 'LSN_summary']
		const filteredReportFields = reportFields.value.filter((field) => {
			return !(calculationCPTIds.length === 1 && summaryFields.includes(field))
		})
		if (!calculationCPTIds.length) return ''
		const calculationCPTIdsQuery = calculationCPTIds.map((id) => `id=${id}`).join('&')
		const filteredReportFieldsQuery = filteredReportFields
			.map((field) => `field=${field}`)
			.join('&')
		const limitsQuery = Object.entries(limits.value)
			.map(([key, value]) => `limit=${key}:${value}`)
			.join('&')
		return calculationCPTIdsQuery + '&' + filteredReportFieldsQuery + '&' + limitsQuery
	})

	return {
		...baseStore,
		currentStrippedItem,
		loading,
		filterFields,
		exportReportDialog,
		exportReportLoading,
		exportExcelDialog,
		exportExcelLoading,
		reportFields,
		limits,
		resetAll,
		removeItemById,
		addAndRemoveItem,
		fetchOrGetCalculationCPT,
		setCurrentItem,
		setCurrentStrippedItem,
		removeItem,
		removeItemsByCalculationId,
		toggleSelectedItem,
		selectAllItemsByCalculationId,
		clearSelectedItemsByCalculationId,
		selectAllItems,
		clearSelectedItems,
		toggleExportReportDialog,
		toggleExportExcelDialog,
		setLimit,
		exportReport,
		exportExcel,
		getStrippedItems,
		hasCalculationCPT,
		getSelectedItemsByCalculationId,
		getItemsByCalculationId,
		getReportUrlQueryParams
	}
})
